import React, {
  FC, useState, useEffect, Fragment,
} from 'react';
import { ProgressBar } from 'components';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Classes } from '@blueprintjs/core';
import { connect, ConnectedProps } from 'react-redux';
import ModalDialog from 'shared/modal/dialog';
import * as warrantActions from 'xcipio/warrants/warrant-actions';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as globalSelectors from 'global/global-selectors';
import { AuditService, AuditActionType } from 'shared/userAudit/audit-constants';
import { AppToaster } from 'shared/toaster';
import { extractErrorMessage } from 'utils';
import { RootState } from 'data/root.reducers';
import ResponseDetails from '../ResponseDetails';
import './warrant-retry-popup.scss';

const mapState = (state: RootState) => ({
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userDBId: authenticationSelectors.getAuthenticationUserName(state),
  dfId: globalSelectors.getSelectedDeliveryFunctionId(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
});

const mapDispatch = {
  warrantReprovision: warrantActions.warrantReprovision,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>

interface WarrantRetryPopupProps {
  handleConfirm: () => void,
  isOpen: boolean,
  warrant: {
    id: string,
    name: string
  },
  isCalledfromCase?: boolean,
  onReload?: () => void
  onWarrantAction?: () => void
}

export type Props = PropsFromRedux & WarrantRetryPopupProps;

const WarrantRetryPopup: FC<Props> = ({
  isOpen,
  handleConfirm,
  warrant,
  dfId,
  isCalledfromCase,
  onReload,
  warrantReprovision,
  onWarrantAction,
}: Props) => {
  const { t } = useTranslation();
  const [inProgress, setInProgress] = useState(true);
  const [displayMessageDetails, setDisplayMessageDetails] = useState(false);
  const [displayMessageButtonText, setDisplayMessageButtonText] = useState(t('warrant.provisioning.viewDetails'));
  const [isConfirmationPopupOpen, setIsConfirmationPopupOpen] = useState(false);
  const [progressMessage, setProgressMessage] = useState(t('warrant.provisioning.progress'));
  const [result, setResult] = useState([]);
  const [isProgressbarOpen, setIsProgressbarOpen] = useState(false);

  useEffect(() => {
    setIsConfirmationPopupOpen(isOpen);
  }, [isOpen]);

  const renderProgress = () => {
    return (
      <div className="display-message">
        {progressMessage}
      </div>
    );
  };

  const renderActionsButton = () => {
    if (inProgress) return null;
    return (
      <div className="actions-button-container">
        <Button className="action-button" onClick={showOrHideMessageDetails}>{displayMessageButtonText}</Button>
        <Button className="action-button" onClick={onProgressClose} intent="primary" text={t('common.button.ok')} />
      </div>
    );
  };

  const renderConfirmationMessage = () => () => {
    if (isCalledfromCase) {
      return (
        <Fragment>
          <span>{t('warrant.provisioning.retryProvisioningAllTargets', { name: warrant.name })}</span>
          <br />
          <span>
            <b>{t('warrant.provisioning.note')} </b>
            {t('warrant.provisioning.allTargetsProvisioned')}
          </span>
        </Fragment>
      );
    }
    return (
      <Fragment>
        <span>{t('warrant.provisioning.retryProvisioning')}</span>
        <br />
      </Fragment>
    );
  };

  const showOrHideMessageDetails = () => {
    if (displayMessageDetails) {
      setDisplayMessageButtonText(t('warrant.provisioning.viewDetails'));
    } else {
      setDisplayMessageButtonText(t('warrant.provisioning.hideDetails'));
    }
    setDisplayMessageDetails(!displayMessageDetails);

  };

  const onSubmit = () => {
    setIsConfirmationPopupOpen(false);
    setIsProgressbarOpen(true);

    const { id, name }: any = warrant;
    const promise: any = warrantReprovision(dfId, id, name, {});
    promise.then((response: any) => {
      const { data } = response.value;
      setInProgress(false);
      if (data.error) {
        setProgressMessage(t('warrant.provisioning.completed', { message: data.error.message }));
        setResult(data.resultDetail);
      }
    }).catch((error: any) => {
      AppToaster.show({
        icon: 'error',
        intent: 'danger',
        timeout: 0,
        message: t('common.toast.error', { error: extractErrorMessage(error) }),
      });
    });
  };

  const onConfirmationClose = () => {
    setIsConfirmationPopupOpen(false);
    handleConfirm();
  };

  const onProgressClose = () => {
    setIsProgressbarOpen(false);
    setInProgress(true);
    setDisplayMessageDetails(false);
    setProgressMessage(t('warrant.provisioning.progress'));
    handleConfirm();
    if (onReload) {
      onReload();
    }
    if (onWarrantAction) {
      onWarrantAction();
    }
  };

  return (
    <div data-testid="WarrantRetryPopup">
      <ModalDialog
        width="500px"
        isOpen={isConfirmationPopupOpen}
        title={t('warrant.provisioning.title')}
        customComponent={renderConfirmationMessage()}
        actionText={t('warrant.provisioning.action')}
        onSubmit={onSubmit}
        onClose={onConfirmationClose}
      />
      <Dialog
        onClose={onProgressClose}
        isOpen={isProgressbarOpen}
        style={{ minWidth: '550px' }}
        canEscapeKeyClose={false}
        canOutsideClickClose={false}
      >
        <div className={Classes.DIALOG_BODY}>
          {renderProgress()}
          <ProgressBar
            value={inProgress ? 1 / 4 : 1}
            animate={inProgress}
          />
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          {renderActionsButton()}
          {
            displayMessageDetails && <ResponseDetails responses={result} />
          }
        </div>
      </Dialog>
    </div>
  );
};

export default connector(WarrantRetryPopup);
